import {
  LineAdditionalServiceListItem,
  LineAdditionalServiceStepName,
  LineAdditionalServiceType,
  LineAdditionalServiceCategory,
  LineAdditionalServicePaymentRequest,
  ILineAdditionalServicesNotesDeliveryAddress
} from "./interfaces";

import { PaymentMethodType } from "@/common/types/PaymentMethod.types";
import { PaymentRequestStatus } from "@/common/types/PaymentRequestTypes";
import { formatDateAndTimeToString } from "@/utils/FormatDate";

const lineAdditionalServiceFulfillmentStatusLabels: Record<
  LineAdditionalServiceType,
  string
> = {
  [LineAdditionalServiceType.DATA_BONUS]: "Lançado",
  [LineAdditionalServiceType.PHYSICAL_CHIP]: "Enviado",
  [LineAdditionalServiceType.ESIM]: "Enviado"
};

export function getLineAdditionalServiceStatusLabel(
  service: LineAdditionalServiceListItem
): string {
  if (isLineAdditionalServiceStepTerminal(service)) {
    return service.currentStep.internalName ===
      LineAdditionalServiceStepName.DELIVERED
      ? lineAdditionalServiceFulfillmentStatusLabels[service.type]
      : service.currentStep.friendlyName;
  }

  return (service.nextStep ?? service.currentStep).friendlyName;
}

export function isLineAdditionalServiceStepTerminal(
  service: Pick<LineAdditionalServiceListItem, "currentStep">
): boolean {
  return [
    LineAdditionalServiceStepName.CANCELED,
    LineAdditionalServiceStepName.DELIVERED
  ].includes(service.currentStep.internalName);
}

export const lineAdditionalServiceCategoryLabels: Record<
  LineAdditionalServiceCategory,
  string
> = {
  [LineAdditionalServiceCategory.ACTIVATION_C]: "2ª via de Ativação C",
  [LineAdditionalServiceCategory.DATA_BONUS]: "Bônus de internet"
};

export const lineAdditionalServiceTypeLabels: Record<
  LineAdditionalServiceType,
  string
> = {
  [LineAdditionalServiceType.DATA_BONUS]:
    lineAdditionalServiceCategoryLabels[
      LineAdditionalServiceCategory.DATA_BONUS
    ],
  [LineAdditionalServiceType.PHYSICAL_CHIP]:
    lineAdditionalServiceCategoryLabels[
      LineAdditionalServiceCategory.ACTIVATION_C
    ],
  [LineAdditionalServiceType.ESIM]:
    lineAdditionalServiceCategoryLabels[
      LineAdditionalServiceCategory.ACTIVATION_C
    ]
};

export const paymentRequestStatusFormatters: Record<
  PaymentRequestStatus,
  (paymentRequest: LineAdditionalServicePaymentRequest) => string
> = {
  [PaymentRequestStatus.COMPLETED]: ({ completedAt }) =>
    completedAt ? `Pago em ${formatDateAndTimeToString(completedAt)}` : "Pago",

  [PaymentRequestStatus.EXPIRED]: ({ expiresAt }) =>
    expiresAt
      ? `Expirado em ${formatDateAndTimeToString(expiresAt)}`
      : "Expirado",

  [PaymentRequestStatus.PROCESSING]: () => "Aguardando pagamento",

  [PaymentRequestStatus.PENDING_INTEGRATION]: () =>
    "Aguardando integração do pagamento",

  [PaymentRequestStatus.CANCELED]: () => "Cobrança cancelada",

  [PaymentRequestStatus.REFUND_REQUESTED]: () => "Reembolso solicitado",

  [PaymentRequestStatus.WAITING_REFUND_REQUEST]: () => "Reembolso solicitado",

  [PaymentRequestStatus.REFUNDED]: ({ refundedAt }) =>
    refundedAt
      ? `Reembolsado em ${formatDateAndTimeToString(refundedAt)}`
      : "Reembolsado",

  [PaymentRequestStatus.REFUNDED_PARTIAL]: ({ refundedAt }) =>
    refundedAt
      ? `Reembolso parcial em ${formatDateAndTimeToString(refundedAt)}`
      : "Reembolso parcial",

  [PaymentRequestStatus.REFUND_NOT_MADE]: () => "Reembolso não realizado"
};

export const chipReplacementValueInCents = 2990;
export const dataBonusValuePerGbInCents = 500;
export const dataBonusAllowedCarriers = ["VIVO"];
export const paymentLinkPollingIntervalInMilliseconds = 3000;
export const paymentLinkPollingMaximumAttempts = 10;
export const LINE_ADDITIONAL_SERVICES_QUERY_TOKEN = "line-additional-services";
export const HAND_DELIVERY_TRACKING_CODE = "Entrega em mãos";

export const lineAdditionalServiceFormDefaultValues = {
  type: LineAdditionalServiceType.PHYSICAL_CHIP,
  isChargeExempt: false,
  dataBonusGb: 5,
  paymentMethod: PaymentMethodType.INVOICE_DEBIT,
  notes: {
    details: "",
    deliveryAddress: {
      zipCode: "",
      city: "",
      state: "",
      street: "",
      neighborhood: "",
      number: "",
      note: ""
    },
    eSimInfo: {
      IMEI: "",
      EID: "",
      deviceModel: ""
    }
  }
} as const;

export const physicalChipDeliveryFormDefaultValues = {
  iccid: "",
  deliveryByMail: true,
  trackingCode: "",
  rescueProtocol: ""
} as const;

export const lineAdditionalServicePaymentMethodOptions = [
  { key: PaymentMethodType.INVOICE_DEBIT, text: "Em Fatura" },
  { key: PaymentMethodType.PIX, text: "Pix" }
];

export const simTypeOptions = [
  { key: LineAdditionalServiceType.PHYSICAL_CHIP, text: "Chip físico" },
  { key: LineAdditionalServiceType.ESIM, text: "eSIM" }
];

export const formatDeliveryAddress = (
  deliveryAddress: ILineAdditionalServicesNotesDeliveryAddress
): string => {
  const street = [deliveryAddress.street, deliveryAddress.number]
    .filter(Boolean)
    .join(", ");
  const note = deliveryAddress.note ? ` (${deliveryAddress.note})` : "";

  return `${street} - ${deliveryAddress.neighborhood}, ${deliveryAddress.city}/${deliveryAddress.state} - ${deliveryAddress.zipCode}${note}`;
};

export const gbsAmountOptions = [1, 2, 3, 5, 10, 15, 20];
